/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ClipboardScorePenaltyMultipliers } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getOrdinalPostfix } from "@src/appV2/lib/utils/formatNumbers";
import type { ButtonProps } from "@src/appV2/redesign/components/Button";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useIsJobsEnabled } from "@src/appV2/redesign/PlacementCandidate/useIsJobsEnabled";
import { PLACEMENT_PATHS } from "@src/appV2/redesign/Placements/paths";
import { useCanBookLimitedAccessShifts } from "@src/appV2/redesign/ShiftDiscovery/LimitedAccess/hooks";
import { LIMITED_ACCESS_INFO_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/LimitedAccess/paths";
import {
  CbhIcon,
  type CbhIconProps,
  Divider,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { uniqueId } from "lodash";
import pluralize from "pluralize";
import { Fragment, type ReactNode } from "react";
import { useHistory } from "react-router-dom";

import { useWorkerEtaImprovementsConfig } from "../../hooks/useWorkerEtaImprovementsConfig";
import {
  CLIPBOARD_SCORE_ADJUSTMENTS_FULL_PATH,
  CLIPBOARD_SCORE_NEVER_NO_SHOW_FULL_PATH,
} from "../../utils/paths";
import { getPenaltyMessage } from "../ActiveMultiplierInfo";
import { NEVER_NO_SHOW_SOURCE } from "../NeverNoShow/Page";

export interface InfoSectionProps {
  iconType: CbhIconProps["type"];
  buttonType?: ButtonProps["variant"];
  title: string;
  description: string | ReactNode;
  color?: CbhIconProps["color"];
  ctaLabel?: string;
  onCtaClick?: () => void;
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

interface InfoProps {
  score: number;
  isWorkerRestricted: boolean;
  areDynamicPenaltiesEnabled: boolean;
  hasWorkedShifts: boolean;
  accessMetadata?: {
    basketSize: number;
    scoreThreshold: number;
  };
  multipliers?: ClipboardScorePenaltyMultipliers;
  restrictionsBottomSheet: UseModalState;
  isFinalRestrictionNext: boolean;
  nextRestrictionDurationInDays: number;
  restrictionCount: number;
  finalRestrictionEvalWindow: number;
}

export function InfoSection(props: InfoSectionProps) {
  const { iconType, buttonType, title, description, ctaLabel, color, onCtaClick, secondaryAction } =
    props;

  return (
    <Stack direction="column" spacing={5}>
      <Stack direction="row" alignItems="left" width="100%">
        <Stack paddingRight={5} justifyContent="flex-start" alignItems="center">
          <CbhIcon type={iconType} color={color} />
        </Stack>
        <Stack spacing={2} justifyContent="left" alignItems="left" textAlign="left">
          <Text bold variant="h6" color={color}>
            {title}
          </Text>
          {typeof description === "string" ? (
            <Text variant="body2" color={color}>
              {description}
            </Text>
          ) : (
            description
          )}
        </Stack>
      </Stack>
      {ctaLabel && (
        <Stack justifyContent="center" alignItems="center" spacing={3}>
          <Button fullWidth variant={buttonType ?? "outlined"} size="medium" onClick={onCtaClick}>
            {ctaLabel}
          </Button>
          {secondaryAction && (
            <TextButton onClick={secondaryAction.onClick}>{secondaryAction.label}</TextButton>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export function Info(props: InfoProps) {
  const {
    score,
    multipliers,
    isWorkerRestricted,
    areDynamicPenaltiesEnabled,
    hasWorkedShifts,
    accessMetadata,
    restrictionsBottomSheet,
    isFinalRestrictionNext,
    nextRestrictionDurationInDays,
    restrictionCount,
    finalRestrictionEvalWindow,
  } = props;

  const history = useHistory();
  const { openArticle } = useZendeskArticle();
  const { limitedStatusSupportLink, clipboardScoreSupportLink } = useSupportLinks();
  const { isClipboardScorePageChangesEnabled } = useWorkerEtaImprovementsConfig();
  const isJobsEnabled = useIsJobsEnabled();
  const canBookLimitedAccessShifts = useCanBookLimitedAccessShifts();

  useLogEffect(
    APP_V2_USER_EVENTS.LIMITED_ACCESS_SCORE_PAGE_VIEWED,
    {},
    { enabled: canBookLimitedAccessShifts }
  );

  const restrictionCountLabel = `${restrictionCount}${getOrdinalPostfix(restrictionCount)}`;

  function getInfoSections() {
    const restrictedSection: InfoSectionProps = {
      title: "While you're Restricted...",
      description: (
        <>
          <Text variant="body2">
            You won&apos;t be able to book shifts. However, you can still receive and accept Shift
            invites as usual.
          </Text>
          {isJobsEnabled && (
            <Text semibold variant="body2">
              This restriction does not impact your access to Jobs on Clipboard: you can still apply
              and interview for permanent positions with workplaces.
            </Text>
          )}
        </>
      ),
      iconType: "remove",
      buttonType: "contained",
      ctaLabel: isJobsEnabled ? "Browse Jobs" : undefined,
      onCtaClick: () => {
        history.push(PLACEMENT_PATHS.ROOT);
      },
    };

    const limitedAccessSection: InfoSectionProps = {
      title: "While you have Limited Access...",
      description: (
        <Stack spacing={3}>
          <Text variant="body2">
            You can claim same-day shifts and still receive Shift invites as usual
            {isJobsEnabled ? ", and apply or interview for permanent positions on Clipboard." : "."}
          </Text>
          <Text semibold variant="body2">
            If your score drops to 0, you&apos;ll return to being fully Restricted.
          </Text>
        </Stack>
      ),
      iconType: "info",
      buttonType: "contained",
      ctaLabel: "Learn more",
      onCtaClick: () => {
        history.push(LIMITED_ACCESS_INFO_FULL_PATH);
      },
    };

    const limitedSection: InfoSectionProps = {
      title: `${(accessMetadata?.scoreThreshold ?? 0) - score} pts to unlock unlimited access`,
      description: (
        <Text variant="body2">
          New accounts have a{" "}
          <Text inline variant="body2" sx={{ fontWeight: 700 }}>
            limited selection
          </Text>{" "}
          of shifts and can only book{" "}
          <Text inline variant="body2" sx={{ fontWeight: 700 }}>
            {accessMetadata?.basketSize} {pluralize("shift", accessMetadata?.basketSize)} at a time
          </Text>
          . You unlock unlimited access at {accessMetadata?.scoreThreshold} points.
        </Text>
      ),
      iconType: "unlock",
      ctaLabel: "Learn more about this status",
      onCtaClick: async () => {
        await openArticle(limitedStatusSupportLink);
      },
    };

    const adjustmentsSection: InfoSectionProps = {
      title: "How does my score change?",
      description: (
        <Stack spacing={3}>
          <Text variant="body2">
            Show up on time. Avoid cancelling, but if plans change, cancel early. Your score has no
            limit!
          </Text>
          <Text variant="body2">
            {areDynamicPenaltiesEnabled &&
              hasWorkedShifts &&
              isDefined(multipliers) &&
              getPenaltyMessage(multipliers)}
          </Text>
        </Stack>
      ),
      iconType: "trending-up-bars",
      buttonType: "contained",
      ctaLabel: "View bonuses and penalties",
      onCtaClick: () => {
        history.push(CLIPBOARD_SCORE_ADJUSTMENTS_FULL_PATH);
      },
    };

    const priorityAccessSection: InfoSectionProps = {
      title: "Higher score = earlier access",
      description:
        "When shifts are posted, they're offered first to professionals with higher scores.",
      iconType: "hourglass",
      ctaLabel: "Learn about Priority Access",
      onCtaClick: async () => {
        await openArticle(clipboardScoreSupportLink);
      },
    };

    const restrictionsSection: InfoSectionProps = {
      title: "Keep your score above zero",
      description: (
        <Stack spacing={3}>
          <Text variant="body2" color={(theme) => theme.palette.error.main}>
            If your score reaches zero, you’ll be removed from your shifts and temporarily unable to
            book.
          </Text>
          <Text variant="body2" color={(theme) => theme.palette.error.main}>
            {isFinalRestrictionNext &&
              `Your next Restriction will last for ${nextRestrictionDurationInDays} days, since it would be the ${restrictionCountLabel} instance within a ${finalRestrictionEvalWindow} month period.`}
          </Text>
        </Stack>
      ),
      color: (theme) => theme.palette.error.main,
      iconType: "triangle-exclamation",
      ctaLabel: "Learn about Restrictions",
      onCtaClick: () => {
        restrictionsBottomSheet.openModal();
      },
    };

    const neverNoShowSection: InfoSectionProps = {
      title: "Never No Show",
      description: (
        <Text variant="body2">
          No Shows lead to Instant Restrictions. Learn more how you can avoid being marked as a No
          Show.
        </Text>
      ),
      iconType: "stop-watch",
      ctaLabel: "Learn about No Shows",
      onCtaClick: () => {
        history.push(CLIPBOARD_SCORE_NEVER_NO_SHOW_FULL_PATH, {
          source: NEVER_NO_SHOW_SOURCE.CLIPBOARD_SCORE_PAGE,
        });
      },
    };

    return [
      ...(canBookLimitedAccessShifts ? [limitedAccessSection] : []),
      ...(isWorkerRestricted && !canBookLimitedAccessShifts ? [restrictedSection] : []),
      ...(!isWorkerRestricted && isDefined(accessMetadata) ? [limitedSection] : []),
      adjustmentsSection,
      priorityAccessSection,
      ...(canBookLimitedAccessShifts ? [] : [restrictionsSection]),
      ...(isClipboardScorePageChangesEnabled ? [neverNoShowSection] : []),
    ];
  }

  const infoSections = getInfoSections();

  return (
    <Stack spacing={8} paddingY={8} paddingX={2}>
      {infoSections.map((section, index) => (
        <Fragment key={uniqueId("info-")}>
          <InfoSection {...section} />
          {index < infoSections.length - 1 && <Divider />}
        </Fragment>
      ))}
    </Stack>
  );
}
/* eslint-enable max-lines */
